/**
 * STK-352 — generic recap detail action button.
 *
 * Renders one `ButtonSpec` from the button table. Tone → color is the only
 * presentation logic here; behavior comes entirely from the `onClick` the
 * caller wires to a transition. A spec with no `transition` (and no `onClick`)
 * renders as a static status pill (confirmed / exported / confirming).
 */
import { Loader2, Sparkles, Check, X, Trash2, RotateCcw } from "lucide-react";
import { Button } from "@stockline/ui/components/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import type { ButtonSpec, ButtonTone } from "./buttonTable";

export type ActionBarVariant = "default" | "hero";

// One unified soft-pill system: light fill (50), strong border (400) + text
// (700). Color carries the semantic — orange = validate/export, blue = confirm,
// violet = AI accept, red = reject/cancel/delete. Static status pills keep the
// softer 300 border + a default/wait cursor.
const TONE_CLASS: Record<ButtonTone, string> = {
  primary: "bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-400",
  confirm: "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-400",
  validate:
    "bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-400",
  cancel: "bg-red-50 hover:bg-red-100 text-red-700 border-red-400",
  // AI actions sit on the violet AI-proposal section — white fill (not violet)
  // so they don't blend violet-on-violet. Strong violet border + text keep them
  // on-theme.
  ai: "bg-white hover:bg-violet-50 text-violet-700 border-violet-400",
  "ai-outline": "bg-white hover:bg-violet-50 text-violet-700 border-violet-400",
  destructive: "bg-red-50 hover:bg-red-100 text-red-700 border-red-400",
  neutral: "bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-400",
  confirmed: "bg-blue-50 text-blue-700 border-blue-300 cursor-default",
  exported: "bg-green-50 text-green-700 border-green-300 cursor-default",
  confirming: "bg-gray-50 text-gray-700 border-gray-300 cursor-wait",
  failed: "bg-red-50 hover:bg-red-100 text-red-700 border-red-400",
  ill: "bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-400",
};

/** Ring color fed to `--attention-ring` for the `animate-attention-pulse`. */
const PULSE_RING: Partial<Record<ButtonTone, string>> = {
  primary: "rgba(249, 115, 22, 0.22)",
  validate: "rgba(249, 115, 22, 0.22)",
  cancel: "rgba(239, 68, 68, 0.22)",
};

const SIZE_CLASS: Record<ActionBarVariant, string> = {
  default: "h-7 px-2 text-xs [&_svg]:h-3.5 [&_svg]:w-3.5",
  // h-8 matches every other focus-header control (back, status pill, prev/next
  // nav, messages toggle) so the toolbar reads as one row of equal-height
  // controls instead of a staircase.
  hero: "h-8 px-3 text-sm [&_svg]:h-4 [&_svg]:w-4",
};

/** Leading glyph per button id — keeps icons declarative, not hard-coded. */
function leadingIcon(spec: ButtonSpec, busy: boolean): React.ReactNode {
  if (busy || spec.spin) return <Loader2 className="animate-spin" />;
  switch (spec.id) {
    case "accept-confirm":
    case "accept-ai-pending":
      return <Sparkles aria-hidden="true" />;
    case "validate-confirm":
    case "confirm":
    case "confirm-disabled":
      return <Check aria-hidden="true" />;
    case "delete":
      return <Trash2 aria-hidden="true" />;
    case "restore":
      return <RotateCcw aria-hidden="true" />;
    case "reject":
    case "reject-ai":
    case "reject-ai-proposal":
    case "cancel-modifications":
      return <X aria-hidden="true" />;
    default:
      return null;
  }
}

interface ActionButtonProps {
  spec: ButtonSpec;
  variant: ActionBarVariant;
  /** Absent → static pill (no interaction). */
  onClick?: () => void;
  disabled?: boolean;
  /** Render the leading icon as a spinner. */
  busy?: boolean;
  /**
   * Stop the click from bubbling to ancestors. Default `true` so a button
   * embedded in a clickable row never triggers row navigation. Set `false`
   * when the button is itself the visible child of a popover trigger (failed /
   * ill pills) — those rely on the click reaching the Radix trigger to open
   * the popover, which `stopPropagation` would otherwise swallow.
   */
  stopPropagation?: boolean;
}

export function ActionButton({
  spec,
  variant,
  onClick,
  disabled,
  busy = false,
  stopPropagation = true,
}: ActionButtonProps) {
  const { t } = useTranslation();
  const isStatic = !onClick;
  const isDisabled = disabled || spec.disabled;
  const showPulse = !!spec.pulse && !isDisabled;
  return (
    <Button
      size="sm"
      // `spec.testId` pins the legacy selector the E2E suite resolves
      // (recap-ai-accept, recap-confirm-export-button, …); fall back to a
      // stable id-derived testid otherwise. `data-state` carries the legacy
      // locale-independent pill selector when the spec defines one.
      data-testid={spec.testId ?? `recap-action-${spec.id}`}
      data-attr={spec.testId ?? `recap-action-${spec.id}`}
      // ALSO emit a spec-id-derived testid as a second attribute. Three
      // different specs share ``recap-confirm-export-button`` (the active
      // ``confirm``, the user-mods-staged ``validate-confirm``, and the
      // shadow ``confirm-disabled`` rendered next to Unconfirm) — that
      // umbrella selector picks any of them via ``.first()`` and the E2E
      // ends up clicking the wrong one. ``data-recap-action`` is stable,
      // unambiguous, and selectable via Playwright's
      // ``page.getByTestId("recap-action-confirm")``.
      data-recap-action={spec.id}
      data-action={spec.id}
      data-state={spec.dataState}
      disabled={isDisabled}
      onClick={
        onClick
          ? (e) => {
              if (stopPropagation) e.stopPropagation();
              onClick();
            }
          : undefined
      }
      style={
        showPulse
          ? ({
              "--attention-ring": PULSE_RING[spec.tone],
            } as React.CSSProperties)
          : undefined
      }
      className={cn(
        SIZE_CLASS[variant],
        "font-semibold border transition-colors inline-flex items-center gap-1.5",
        TONE_CLASS[spec.tone],
        showPulse && "animate-attention-pulse",
        isStatic && "pointer-events-none",
        isDisabled && "opacity-50 cursor-not-allowed pointer-events-none",
      )}
    >
      {leadingIcon(spec, busy)}
      <span>{t(spec.labelKey, spec.fallbackLabel)}</span>
    </Button>
  );
}
